(function () {
  const MOBILE_GALLERY_QUERY = '(max-width: 989px)';

  class ProductMediaGallery extends HTMLElement {
    constructor() {
      super();
      this.sliderElement = this.querySelector('[data-product-gallery-slider], .product-gallery__slider');
      this.track = this.querySelector('.product-gallery__track');
      this.autoplayButton = this.querySelector('.product-gallery__autoplay-toggle');
      this.currentCounter = this.querySelector('[data-gallery-current]');
      this.mediaQuery = window.matchMedia(MOBILE_GALLERY_QUERY);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.selectedMediaId = null;
      this.swiper = null;
      this.autoplayPaused = false;

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleAutoplayToggle = this.handleAutoplayToggle.bind(this);
      this.handleDirectInteraction = this.handleDirectInteraction.bind(this);
    }

    connectedCallback() {
      if (this.initialized || !this.sliderElement || !this.track) return;
      this.initialized = true;
      this.isQuickView = Boolean(this.closest('quick-add-modal'));

      this.addMediaQueryListener(this.mediaQuery, this.handleBreakpointChange);
      this.addMediaQueryListener(this.reducedMotionQuery, this.handleBreakpointChange);
      this.autoplayButton?.addEventListener('click', this.handleAutoplayToggle);
      this.sliderElement.addEventListener('pointerdown', this.handleDirectInteraction, { passive: true });
      this.sliderElement.addEventListener('focusin', this.handleDirectInteraction);

      this.handleBreakpointChange();
    }

    disconnectedCallback() {
      this.destroyMobileGallery();
      this.removeMediaQueryListener(this.mediaQuery, this.handleBreakpointChange);
      this.removeMediaQueryListener(this.reducedMotionQuery, this.handleBreakpointChange);
      this.autoplayButton?.removeEventListener('click', this.handleAutoplayToggle);
      this.sliderElement?.removeEventListener('pointerdown', this.handleDirectInteraction);
      this.sliderElement?.removeEventListener('focusin', this.handleDirectInteraction);
      this.initialized = false;
    }

    addMediaQueryListener(query, listener) {
      if (query.addEventListener) query.addEventListener('change', listener);
      else query.addListener(listener);
    }

    removeMediaQueryListener(query, listener) {
      if (query.removeEventListener) query.removeEventListener('change', listener);
      else query.removeListener(listener);
    }

    get slides() {
      return Array.from(this.track?.children || []).filter((slide) =>
        slide.classList.contains('product-gallery__slide')
      );
    }

    handleBreakpointChange() {
      if (this.isQuickView) {
        this.initQuickViewGallery();
        return;
      }

      if (!this.mediaQuery.matches) {
        this.destroyMobileGallery();
        this.promoteSelectedMedia();
        return;
      }

      this.initMobileGallery();
    }

    initMobileGallery() {
      if (this.swiper || this.slides.length < 2) {
        this.updateCounter(this.swiper?.activeIndex || 0);
        return;
      }

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', () => this.initMobileGallery(), { once: true });
        return;
      }

      const autoplayEnabled =
        this.dataset.mobileAutoplay === 'true' && !this.reducedMotionQuery.matches;
      const autoplayDelay = Number.parseInt(this.dataset.autoplayDelay, 10) || 4500;
      const canLoop = this.slides.length > 2;

      this.swiper = new window.Swiper(this.sliderElement, {
        slidesPerView: 1.08,
        spaceBetween: 12,
        speed: 620,
        loop: canLoop,
        rewind: !canLoop,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        autoplay: autoplayEnabled
          ? {
              delay: autoplayDelay,
              disableOnInteraction: true,
              pauseOnMouseEnter: true,
            }
          : false,
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.previousLabel || 'Previous slide',
          nextSlideMessage: this.dataset.nextLabel || 'Next slide',
        },
        on: {
          init: (swiper) => this.updateCounter(swiper.realIndex),
          slideChange: (swiper) => {
            window.pauseAllMedia?.();
            this.updateCounter(swiper.realIndex);
          },
          autoplayStart: () => {
            this.autoplayPaused = false;
            this.updateAutoplayButton();
          },
          autoplayStop: () => {
            this.autoplayPaused = true;
            this.updateAutoplayButton();
          },
        },
      });

      if (this.selectedMediaId) this.selectMedia(this.selectedMediaId, false);
      this.updateAutoplayButton();
    }

    initQuickViewGallery() {
      if (this.swiper || this.slides.length < 2) return;

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', () => this.initQuickViewGallery(), { once: true });
        return;
      }

      this.swiper = new window.Swiper(this.sliderElement, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 420,
        rewind: true,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        on: {
          slideChange: () => window.pauseAllMedia?.(),
        },
      });

      if (this.selectedMediaId) this.selectMedia(this.selectedMediaId, false);
    }

    destroyMobileGallery() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.autoplayPaused = false;
      this.updateAutoplayButton();
    }

    handleDirectInteraction() {
      if (!this.swiper?.autoplay?.running) return;
      this.swiper.autoplay.stop();
    }

    handleAutoplayToggle() {
      if (!this.swiper?.autoplay) return;

      if (this.swiper.autoplay.running) {
        this.swiper.autoplay.stop();
      } else if (!this.reducedMotionQuery.matches) {
        this.swiper.autoplay.start();
      }
      this.updateAutoplayButton();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;

      const canAutoplay =
        this.mediaQuery.matches &&
        this.dataset.mobileAutoplay === 'true' &&
        !this.reducedMotionQuery.matches &&
        this.slides.length > 1;
      const isPaused = !this.swiper?.autoplay?.running;

      this.autoplayButton.hidden = !canAutoplay;
      this.autoplayButton.dataset.state = isPaused ? 'paused' : 'playing';
      this.autoplayButton.setAttribute('aria-pressed', String(isPaused));
      this.autoplayButton.setAttribute(
        'aria-label',
        isPaused ? this.autoplayButton.dataset.playLabel : this.autoplayButton.dataset.pauseLabel
      );
    }

    updateCounter(index) {
      if (!this.currentCounter) return;
      this.currentCounter.textContent = String(index + 1).padStart(2, '0');
    }

    selectMedia(mediaId, animate = true) {
      this.selectedMediaId = mediaId;
      const slide = this.slides.find((item) => item.dataset.mediaId === mediaId);
      if (!slide) return;

      if (this.isQuickView) {
        this.initQuickViewGallery();
        const index = this.slides.indexOf(slide);
        this.swiper?.slideTo(index, animate ? 420 : 0);
        return;
      }

      if (this.mediaQuery.matches) {
        this.initMobileGallery();
        const index = Number.parseInt(slide.dataset.galleryIndex, 10);
        if (Number.isNaN(index)) return;
        if (this.swiper?.params.loop) {
          this.swiper.slideToLoop(index, animate ? 420 : 0);
        } else {
          this.swiper?.slideTo(index, animate ? 420 : 0);
        }
        this.updateCounter(index);
        return;
      }

      this.promoteSelectedMedia();
    }

    promoteSelectedMedia() {
      if (!this.selectedMediaId) return;
      const slide = this.slides.find((item) => item.dataset.mediaId === this.selectedMediaId);
      if (!slide || slide === this.track.firstElementChild) return;
      this.track.prepend(slide);
      this.slides.forEach((item, index) => {
        item.dataset.galleryIndex = index;
      });
      this.updateCounter(0);
    }
  }

  class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.stageElement = this.querySelector('[data-product-modal-slider]');
      this.thumbsElement = this.querySelector('[data-product-modal-thumbs]');
      this.currentCounter = this.querySelector('[data-modal-current]');
      this.thumbButtons = Array.from(this.querySelectorAll('[data-modal-thumb-id]'));
      this.mainSwiper = null;
      this.thumbSwiper = null;

      this.querySelector('[data-product-modal-close]')?.addEventListener('click', () => this.hide());
      this.thumbButtons.forEach((button, index) => {
        button.addEventListener('click', () => this.mainSwiper?.slideTo(index));
      });
    }

    hide() {
      super.hide();
      document.body.classList.remove('product-gallery-modal-open');
      window.pauseAllMedia?.();
    }

    show(opener) {
      super.show(opener);
      document.body.classList.add('product-gallery-modal-open');

      if (!this.stageElement) {
        this.showLegacyActiveMedia(opener?.dataset.mediaId);
        return;
      }

      this.ensureSliders();

      const requestedMediaId = opener?.dataset.mediaId;
      const index = this.slides.findIndex(
        (slide) => slide.dataset.modalMediaId === requestedMediaId
      );

      requestAnimationFrame(() => {
        this.mainSwiper?.update();
        this.thumbSwiper?.update();
        this.mainSwiper?.slideTo(Math.max(index, 0), 0);
        this.updateActiveMedia(Math.max(index, 0));
      });
    }

    showLegacyActiveMedia(mediaId) {
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('active', element.dataset.mediaId === mediaId);
      });
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      activeMedia?.scrollIntoView();

      if (
        activeMedia?.nodeName === 'DEFERRED-MEDIA' &&
        activeMedia.querySelector('template')?.content?.querySelector('.js-youtube')
      ) {
        activeMedia.loadContent();
      }
    }

    get slides() {
      return Array.from(this.querySelectorAll('.product-media-modal__slide'));
    }

    ensureSliders() {
      if (this.mainSwiper || !this.stageElement || typeof window.Swiper === 'undefined') return;

      if (this.thumbsElement) {
        this.thumbSwiper = new window.Swiper(this.thumbsElement, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true,
          centerInsufficientSlides: true,
        });
      }

      this.mainSwiper = new window.Swiper(this.stageElement, {
        slidesPerView: 1,
        spaceBetween: 24,
        speed: 480,
        rewind: true,
        grabCursor: this.slides.length > 1,
        watchOverflow: true,
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        navigation: {
          prevEl: this.querySelector('[data-product-modal-prev]'),
          nextEl: this.querySelector('[data-product-modal-next]'),
        },
        on: {
          init: (swiper) => this.updateActiveMedia(swiper.activeIndex),
          slideChange: (swiper) => {
            window.pauseAllMedia?.();
            this.updateActiveMedia(swiper.activeIndex);
          },
        },
      });
    }

    updateActiveMedia(index) {
      if (this.currentCounter) this.currentCounter.textContent = String(index + 1);

      this.thumbButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        if (isActive) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.thumbSwiper?.slideTo(index);
    }
  }

  if (!customElements.get('product-media-gallery')) {
    customElements.define('product-media-gallery', ProductMediaGallery);
  }

  if (!customElements.get('product-modal')) {
    customElements.define('product-modal', ProductModal);
  }
})();
